import { ChevronLeft, ChevronRight } from "react-feather"
import { useTranslation } from "react-i18next"
import { Transition } from "@headlessui/react"
import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"

import { ChunkyUnderline } from "~/components/ChunkyUnderline"
import { IconButton } from "~/components/IconButton"
import type { PreviewProps } from "~/components/PublishedItemPreview"
import { PublishedItemPreview } from "~/components/PublishedItemPreview"

export type ArticleGalleryProps = {
  articles: PreviewProps[]
}

export const ArticleGallery: React.FC<ArticleGalleryProps> = ({ articles }) => {
  const { t } = useTranslation("contentThemePage")

  const hasMultipleArticles = articles.length > 1
  const [emblaRef, emblaAPI] = useEmblaCarousel({
    loop: true,
    draggable: false,
    skipSnaps: false,
  })

  if (!articles.length) return null

  return (
    <div>
      <div className="prose mb-4 dark:prose-dark">
        <h4>
          <ChunkyUnderline>{t("authorCard.posts.title")}</ChunkyUnderline>
        </h4>
      </div>

      <div className="flex flex-wrap items-center justify-between md:flex-nowrap md:space-x-2">
        {hasMultipleArticles && (
          <IconButton
            className="order-2 shrink-0 md:order-1 md:flex"
            onClick={() => emblaAPI?.scrollPrev(true)}
          >
            <ChevronLeft />
          </IconButton>
        )}

        <div
          className="embla order-1 max-w-full basis-full overflow-hidden md:order-2"
          ref={emblaRef}
        >
          <Transition
            as="ul"
            className={clsx("embla__container", "flex w-full duration-1000")}
            enterFrom={clsx("opacity-0")}
            enterTo={clsx("opacity-100")}
          >
            {articles.map((article) => {
              return (
                <li
                  key={article.href}
                  className="embla__slide relative mx-1 shrink-0 grow-0 basis-full"
                >
                  <PublishedItemPreview {...article} />
                </li>
              )
            })}
          </Transition>
        </div>

        {hasMultipleArticles && (
          <IconButton
            className="order-3 shrink-0 md:flex"
            onClick={() => emblaAPI?.scrollNext(true)}
          >
            <ChevronRight />
          </IconButton>
        )}
      </div>
    </div>
  )
}
